import { Box, Dialog, Drawer, IconButton, Table, TableBody, TableCell, TableContainer, TableRow, Typography, useTheme } from '@mui/material'
import React, { useState } from 'react'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'

import { convertToCDNLink, formatDate, formatVideoDuration, getTransformedGroupDetails, Icons } from '../../utils/utilities'
import { isUrlAccessible } from '../../shared/utils/utilities'
import { useSettingsStore } from '../../store/settings.store'
import CustomImage from '../../shared/components/common/CustomImage'
import Message from '../../shared/components/common/Message'
import VideoPlayer from '../common/video-player/VideoPlayer'
import TableHeader from '../common/TableHeader'
import CustomTabs from '../common/CustomTabs'
import CustomChip from '../common/CustomChip'
import Progress from '../common/Progress'
import api from '../../service/api'

const participantsColumns = [
    { name: "SL. NO.", width: "90px", padding: "10px 20px" },
    { name: "User ID", width: "130px", padding: "10px 20px" },
    { name: "Name", width: "250px", padding: "10px 20px" },
    { name: "Video Watch %", width: "150px", padding: "10px 20px" },
    { name: "Quiz Attempted", width: "150px", padding: "10px 20px" },
]

const ConflictRecordingCard = ( { video, onPlay } ) => {

    const { palette, border } = useTheme()

    return (
        <Box onClick={() => onPlay( video )} sx={{ cursor: "pointer", ":hover": { borderColor: palette.text.secondary } }} border={border[1]} justifyContent="space-between" padding="5px 10px" display="flex" alignItems="center" gap="10px" borderRadius="5px" bgcolor={palette.contentBg}>
            <Box sx={{ flexDirection: { xs: "column", sm: "row" } }} display="flex" flexGrow={1} gap="10px" >

                <Box border={border[1]} borderRadius="5px" overflow="clip" sx={{ aspectRatio: "16 / 9" }} height={{ xs: "auto", sm: "120px" }} position="relative" flexShrink={0}>
                    {video?.thumbnail_metadata?.location ?
                        <CustomImage src={video.thumbnail_metadata?.location} /> :
                        <Box display="flex" alignItems="center" justifyContent="center" height="100%" bgcolor={palette.contentBg} color="text.secondary" fontSize="32px">
                            {Icons.default.OndemandVideoIcon}
                        </Box>
                    }
                    <Box position="absolute" display="flex" alignItems="center" justifyContent="center" sx={{ inset: 0, color: palette.text.primary }} fontSize="40px"  >
                        <Typography fontSize="18px" display="flex" alignItems="center" justifyContent="center" bgcolor={palette.contentBg} width="30px" borderRadius="50%" sx={{ boxShadow: 5, aspectRatio: "1 / 1" }} >{Icons.default.PlayArrowIcon}</Typography>
                    </Box>
                    {video.recording_metadata?.duration && <Box position="absolute" display="flex" alignItems="center" justifyContent="center" sx={{ bottom: 2, right: 2, color: palette.text.primary }}   >
                        <Typography fontSize="10px" borderRadius="5px" padding="1px 3px" display="flex" alignItems="center" justifyContent="center" bgcolor={palette.contentBg}>{formatVideoDuration( video.recording_metadata.duration )}</Typography>
                    </Box>}
                </Box>

                <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", justifyContent: "flex-start" }}>
                    <Typography className='truncate-at-2' gutterBottom sx={{ fontSize: "1rem", fontWeight: 600 }} component="h3">
                        {video.title}
                    </Typography>
                    <Box gap="5px" alignItems="center" display="flex" flexWrap="wrap">
                        {video.topics_covered?.map( topic => (
                            <CustomChip key={topic} value={topic} />
                        ) )}
                    </Box>
                    <Box marginTop="5px" display="flex" gap="5px" alignItems="center" flexWrap="wrap">
                        {video.expired && <CustomChip color="error" value={`Expired at: ${dayjs( video.endDate ).format( "LLL" )}`} />}
                        {!video.expired && <CustomChip value={`Expires at: ${dayjs( video.endDate ).format( "LLL" )}`} />}
                        <CustomChip value={`Uploaded at: ${dayjs( video.uploaded_at ).format( "LLL" )}`} />
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}

const ConflictingClassSessionDetailsDrawer = ( { open, onClose, details } ) => {

    const [tabValue, setTabValue] = useState( "recordings" )
    const [recordingLink, setRecordingLink] = useState( null )
    const [playingVideo, setPlayingVideo] = useState( null )

    const { palette, border, layout } = useTheme()

    const SettingsStore = useSettingsStore()

    const noRecordingsFound = details?.recordings_found === false

    const sessionDetails = details?.session_details || {}
    const recordings = details?.recordings || []
    const attendance = details?.attendance || {}
    const quiz = attendance?.quiz
    const quizAttemptNotMade = quiz && Object.keys( quiz ).length === 0
    const videoViewers = attendance?.videoViewers || []
    const watchThreshold = +( attendance?.thresholds?.watchProgressAttendanceThreshold || 80 )

    const tabs = [
        { label: "Recordings", value: "recordings", icon: Icons.default.VideocamIcon, count: recordings.length },
        { label: "Quiz", value: "quiz", icon: Icons.default.QuizIcon },
        { label: "Participants", value: "participants", icon: Icons.default.Group, count: videoViewers.length },
    ]

    const playVideo = async ( video ) => {
        try {
            const key = video.recording_key
            if ( !key ) return

            const streamSource = SettingsStore.getSettingValueFromKey( 'hybrid_class_stream_source', 'spaces' )

            if ( streamSource === 'cdn' ) {
                const cdnUrl = convertToCDNLink( video?.recording_metadata?.location )
                if ( await isUrlAccessible( cdnUrl ) ) {
                    setRecordingLink( cdnUrl )
                    setPlayingVideo( video )
                    return
                }
            }
            const { data: { data: url } } = await api.recording.fetchHybridClassRecordingSignedUrl( sessionDetails.id, video.id, { key, register_watch: false } )
            setRecordingLink( url )
            setPlayingVideo( video )
        } catch ( err ) {
            toast( err?.response?.data?.message || "Something went wrong while fetching video!" )
        }
    }

    const closeVideoPlayer = () => {
        setRecordingLink( null )
        setPlayingVideo( null )
    }

    return (
        <Drawer anchor="bottom" open={open} onClose={onClose} slotProps={{ paper: { sx: { borderRadius: "10px 10px 0 0", maxHeight: "85vh", width: "100%" } } }} >

            {recordingLink && <Dialog open={Boolean( recordingLink )} onClose={closeVideoPlayer} slotProps={{ paper: { sx: { maxWidth: "1024px", maxHeight: "90%", width: "100%", aspectRatio: "16 / 9" } } }} >
                <Box position="absolute" zIndex={10} top="0" borderRadius="0 0 0 50%" right="0" bgcolor={palette.common.font + "cc"}>
                    <IconButton onClick={closeVideoPlayer} sx={{ color: palette.common.bg }} >
                        {Icons.default.CloseIcon}
                    </IconButton>
                </Box>
                <VideoPlayer autoPlay key="player" title={playingVideo?.title || ""} containerstyles={{ background: palette.contentBg }} src={recordingLink} />
            </Dialog>}

            <Box sx={{ zIndex: 10 }} borderBottom={border[1]} position="sticky" top="0" bgcolor={palette.background.paper} display="flex" alignItems="flex-start" flexWrap="wrap" justifyContent="space-between" gap="10px" padding="10px 20px" >
                <Box>
                    <Typography variant="h1" fontWeight="700" component="h2" fontSize="16px">{sessionDetails.title || "Conflicting class session"}</Typography>
                    <Box display="flex" marginTop="5px" gap="5px" flexWrap="wrap" alignItems="center">
                        {sessionDetails.starts_at && sessionDetails.ends_at && <CustomChip startIcon={Icons.default.Clock} value={`${formatDate( sessionDetails.starts_at, { format: "MMM D, hh:mm A" } )} - ${formatDate( sessionDetails.ends_at, { format: "hh:mm A" } )}`} />}
                        {sessionDetails.creator_data?.name && <CustomChip startIcon={Icons.default.User} value={sessionDetails.creator_data.name} />}
                        {sessionDetails.time_table_class_details?.subject_name_short && <CustomChip value={sessionDetails.time_table_class_details.subject_name_short} />}
                    </Box>
                </Box>
                <IconButton color='greyed' onClick={onClose} >{Icons.default.CloseIcon}</IconButton>
            </Box>

            <Box className='custom-scrollbar' overflow="auto" flexGrow={1} display="flex" flexDirection="column">

                {noRecordingsFound && <Box padding="20px" flexGrow={1} display="flex" alignItems="center" justifyContent="center">
                    <Message
                        icon={Icons.default.Warning}
                        color="warning"
                        title="Conflicting session found"
                        description="This class has a conflicting session, likely caused by swapping the class between faculty members. Please contact the Alive coordinators to have it removed."
                        actions={[{ label: "Okay", variant: "contained", onClick: onClose }]}
                    />
                </Box>}

                {!noRecordingsFound && <>

                    <Box margin="20px 20px 0 20px" bgcolor={palette.contentBg} padding="10px" borderRadius={layout.borderRadius} border={border[1]}>
                        <Typography display="flex" alignItems="center" gap="10px" variant='subtitle2' fontSize="13px">
                            {Icons.default.Warning} A class session already exists for this slot with recordings and a quiz already assigned. Review the details below.
                        </Typography>
                    </Box>

                    <CustomTabs tabs={tabs} handleTabChange={setTabValue} value={tabValue} variant='stretched' styles={{ width: "100%", marginTop: "20px" }} />

                    <Box padding="20px" display="flex" flexDirection="column" gap={layout.cardsGap}>

                        {tabValue === "recordings" && <Box display="flex" flexDirection="column" gap="10px">
                            {recordings.length === 0 && <Message icon={Icons.default.Warning} color="warning" title="No recordings found for this session. Please contact the Alive coordinators for a clean restart." />}
                            {recordings.map( video => (
                                <ConflictRecordingCard key={video.id} video={video} onPlay={playVideo} />
                            ) )}
                        </Box>}

                        {tabValue === "quiz" && <Box>
                            {!quiz && <Typography padding="20px" textAlign="center" display="block" variant='subtitle2'>No quiz assigned for this session.</Typography>}
                            {quizAttemptNotMade && <Message icon={Icons.default.Warning} color="warning" title="No attempt was made for this quiz." />}
                            {quiz && !quizAttemptNotMade && <Box border={border[1]} borderRadius={layout.borderRadius} overflow="hidden" bgcolor={palette.background.paper}>
                                <Box borderBottom={border[1]} display="flex" flexWrap="wrap" padding="10px 20px" bgcolor={palette.contentBg} alignItems="center" gap={layout.cardsGap}>
                                    <Box flexGrow={1}>
                                        <Typography variant='subtitle2'><small>{formatDate( quiz.start_date, { format: "ll" } )}, {quiz.start_time} - {quiz.end_time}</small></Typography>
                                        {quiz.access_group && <Typography marginTop="-5px" fontSize="14px">{getTransformedGroupDetails( quiz.access_group )?.chip_label}, {getTransformedGroupDetails( quiz.access_group )?.academic_details}</Typography>}
                                    </Box>
                                    <Typography variant='subtitle2'><strong>{quiz.students?.length || 0} attempted</strong></Typography>
                                </Box>
                                <Box padding="10px 20px" display="flex" gap="5px" flexWrap="wrap" alignItems="center">
                                    <CustomChip startIcon={Icons.default.Clock} value={`Duration: ${quiz.quiz_duration}`} />
                                    <CustomChip value={`Max Attempts: ${quiz.maximum_attempts}`} />
                                </Box>
                                {quiz.students?.length > 0 && <Box padding="10px 20px" display="flex" gap="5px" flexWrap="wrap" alignItems="center">
                                    {quiz.students.map( studentId => (
                                        <CustomChip key={studentId} value={studentId} />
                                    ) )}
                                </Box>}
                            </Box>}
                        </Box>}

                        {tabValue === "participants" && <Box>
                            {videoViewers.length === 0 && <Typography padding="20px" textAlign="center" display="block" variant='subtitle2'>No video watch activity recorded for this session.</Typography>}
                            {videoViewers.length > 0 && <TableContainer className='custom-scrollbar' sx={{ maxHeight: "350px", border: border[1], borderRadius: layout.borderRadius, overflow: "auto" }}>
                                <Table stickyHeader>
                                    <TableHeader columns={participantsColumns} />
                                    <TableBody>
                                        {videoViewers.map( ( viewer, index ) => (
                                            <TableRow key={viewer.user_id} sx={{ bgcolor: palette.common.bg }}>
                                                <TableCell sx={{ padding: "5px 20px", borderBottom: border[1] }}>{index + 1}</TableCell>
                                                <TableCell sx={{ padding: "5px 20px", borderBottom: border[1] }}>{viewer.user_id}</TableCell>
                                                <TableCell sx={{ padding: "5px 20px", borderBottom: border[1] }}>{viewer.name}</TableCell>
                                                <TableCell sx={{ padding: "5px 20px", borderBottom: border[1] }}>
                                                    <Box display="flex" alignItems="center" gap="5px">
                                                        <Progress secondaryColor="GrayText" size={20} progress={viewer.average} trackWidth={2} indicatorWidth={2} color={viewer.average >= watchThreshold ? palette.success.main : palette.secondary.main} />
                                                        {Math.min( 100, viewer.average || 0 )}%
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ padding: "5px 20px", borderBottom: border[1] }}>
                                                    <CustomChip startIcon={quiz?.students?.includes( viewer.user_id ) ? Icons.default.CheckCircleIcon : Icons.default.BlockIcon} value={quiz?.students?.includes( viewer.user_id ) ? "Attempted" : "Not Attempted"} />
                                                </TableCell>
                                            </TableRow>
                                        ) )}
                                    </TableBody>
                                </Table>
                            </TableContainer>}
                        </Box>}

                    </Box>
                </>}
            </Box>
        </Drawer>
    )
}

export default ConflictingClassSessionDetailsDrawer
